import { DEFAULT_MODEL } from '../../constants';
import { deleteUndefinedKeysInPlace, toObj } from '../../helpers/data';
import { calculateEventEndAt } from '../../helpers/events';
import { getTimeZoneOffsetFromTimezone } from '../../helpers/time';
import type { File, ID, Optional, User, UserSettings } from '../../models';
import { ActivitySource, type Activity } from '../../models/activity';
import {
  CampaignApplicationStatus,
  CampaignApplicationType,
  type AskACoachApplication,
  type CampaignApplication,
} from '../../models/campaign-application';
import type { CreditUsageHistoryCurrent } from '../../models/credit-usage-history';
import type { DistanceType, Event, RaceEvent, SaveEventPayload } from '../../models/event';
import type { TrainingPlan } from '../../models/training-plan';
import type { UserProvider } from '../../models/user-provider';
import { UserProviderStatus, UserProviderType } from '../../models/user-provider';
import type { UserRegistration } from '../../models/user-registration';

export async function getCurrentUserCredit(): Promise<CreditUsageHistoryCurrent> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');

  const userDb = new UserDatabase({
    provider: getClientProvider(),
  });
  const id = await userDb.provider.getCurrentUserId();

  return userDb.getUserCurrentCredit(id as string);
}

export async function updateUser(id: string, user: Optional<User>) {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const userDb = new UserDatabase({
    provider: getClientProvider(),
  });

  return userDb.update(id, user);
}

export async function getUser(userId: ID): Promise<User | null> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');
  const { toObj } = await import('../../helpers/data');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  return userDb.get(userId).then((doc) => toObj<User>(doc));
}

export async function findUserByEmail(email?: string): Promise<User[]> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');
  const { toObj } = await import('../../helpers/data');

  if (!email) {
    return [];
  }

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const query = userDb.provider.query().starWith('email', email).limit(10);
  return userDb.list(query).then((docs) => {
    return docs.map((doc) => toObj<User>(doc) as User);
  });
}

export async function getCurrentUser(): Promise<User | null> {
  const { getClientProvider } = await import('./');

  const provider = getClientProvider();
  const id = await provider.getCurrentUserId();
  if (!id) {
    return null;
  }

  return getUser(id);
}

export async function getProvider(type: UserProviderType): Promise<UserProvider | null> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');
  const { toObj } = await import('../../helpers/data');

  const provider = getClientProvider();
  const id = await provider.getCurrentUserId();

  const userDb = new UserDatabase({
    provider,
  });

  return userDb
    .getUserProviderDb(id as string)
    .get(type)
    .then((doc) => {
      const data = toObj<UserProvider>(doc) as UserProvider;
      if (data?.status === UserProviderStatus.Disconnected) {
        return null;
      }
      return data;
    });
}

export async function updatePushToken(deviceUUID: string, token: string) {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await await import('../../db');

  const provider = getClientProvider();

  const userDb = new UserDatabase({
    provider,
  });

  const userAgent = userDb.provider.getUserAgent();

  return userDb.updatePushToken(deviceUUID, token, userAgent);
}

export async function getCurrentUserProviders(): Promise<UserProvider[]> {
  const { getClientProvider } = await import('./');

  const provider = getClientProvider();
  const id = await provider.getCurrentUserId();

  return getUserProviders(id as string);
}

export async function getUserProviders(userId: ID): Promise<UserProvider[]> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');
  const { toObj } = await import('../../helpers/data');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  return userDb
    .getUserProviderDb(userId)
    .list()
    .then((docs) => {
      return docs.map((doc) => toObj<UserProvider>(doc) as UserProvider);
    });
}

export async function getUserSettings(): Promise<UserSettings> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const provider = getClientProvider();
  const id = await provider.getCurrentUserId();

  const userDb = new UserDatabase({
    provider,
  });

  return userDb.getObj(id as string).then((user) => {
    return (
      user?.settings || {
        model: DEFAULT_MODEL,
        language: 'ja',
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        tzOffset: getTimeZoneOffsetFromTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone),
        supressPostingToStrava: false,
        source: ActivitySource.GARMIN,
        suppressHrRequest: false,
        suppressPostActivitySurvey: false,
        automaticAudioPlay: false,
      }
    );
  });
}

export async function setUserSettings(settings: UserSettings) {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');
  const start = new Date();
  const provider = getClientProvider();
  const id = await provider.getCurrentUserId();
  console.log('[settings] id time:', settings, new Date().getTime() - start.getTime(), 'ms');
  const userDb = new UserDatabase({
    provider,
  });

  const user = await userDb.getObj(id as string);
  console.log('[settings] fetched user time:', user, new Date().getTime() - start.getTime(), 'ms');

  return userDb
    .update(id as string, {
      settings: {
        ...(user?.settings || {
          model: DEFAULT_MODEL,
          language: 'ja',
          tz: 'Asia/Tokyo',
        }),
        ...settings,
      },
    })
    .then(() => {
      console.log('[settings] updated settings time:', new Date().getTime() - start.getTime(), 'ms');
    });
}

export async function getUserRegistration(): Promise<UserRegistration | null> {
  const { getClientProvider } = await import('./');
  const { UserRegistrationDatabase } = await import('../../db/user-registration-database');
  const { toObj } = await import('../../helpers/data');

  const provider = getClientProvider();
  const id = await provider.getCurrentUserId();

  if (!id) {
    return null;
  }

  const regDb = new UserRegistrationDatabase({
    provider,
  });

  return regDb.get(id).then(toObj);
}

export async function getUserLatestHeartRateMetric(userId: ID) {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');
  const { toObj } = await import('../../helpers/data');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const hrMetricsDb = userDb.getHrMetricsDb(userId);
  const query = hrMetricsDb.provider.query().orderBy('createdAt', 'desc').limit(1);

  const docs = await hrMetricsDb.list(query);
  if (docs.length === 0) {
    return null;
  }

  return toObj(docs[0]);
}

export async function loadUsersByProviderType(
  type: UserProviderType
): Promise<{ provider: UserProvider; user: User; providers: UserProvider[] }[]> {
  const { getClientProvider } = await import('./');
  const { UserProviderDatabase } = await import('../../db/user-provider-database');
  const { UserDatabase } = await import('../../db/user-database');
  const { toObj } = await import('../../helpers/data');

  const provider = getClientProvider();
  const providerDb = new UserProviderDatabase({
    provider,
  });
  const userDb = new UserDatabase({
    provider,
  });
  const query = providerDb.provider.groupQuery().where('type', '==', type);

  const providers = await providerDb.list(query).then((docs) => docs.map((doc) => toObj(doc) as UserProvider));

  return Promise.all(
    providers.map(async (prov) => {
      const user = await getUser(prov.parentId as string);
      const providers = await userDb
        .getUserProviderDb(prov.parentId as string)
        .list()
        .then((docs) => docs.map((doc) => toObj(doc) as UserProvider));
      return { provider: prov, user: user as User, providers };
    })
  );
}

export async function getUpcomingUserEvents(userId: ID, from: Date, limit: number = 10): Promise<Event[]> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  return userDb.getUserUpcomingEvents(userId, from, limit);
}

export async function getMainRace(userId: ID): Promise<RaceEvent | null> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  return userDb.getMainRace(userId);
}

export async function getUserEventsInRange({
  userId,
  from,
  to,
}: {
  userId: ID;
  from: Date;
  to: Date;
}): Promise<Event[]> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  return userDb.getUserEventWithinPeriod(userId, from, to);
}

export async function getUserActivitiesInRange({
  userId,
  from,
  to,
  source,
}: {
  userId: ID;
  from: Date;
  to: Date;
  source?: ActivitySource | null;
}): Promise<Activity[]> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  return userDb.getActivitiesWithinPeriod(userId, from, to, source);
}

export async function updateEventForUser(userId: ID, eventId: ID, event: Partial<Event>): Promise<void> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const eventDb = userDb.getEventDb(userId);
  return eventDb.update(eventId, event);
}

export async function deleteEventForUser(userId: ID, eventId: ID): Promise<void> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('../../db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const eventDb = userDb.getEventDb(userId);
  return eventDb.deleteX(eventId);
}

export async function updateUserSettings(userId: ID, settings: Partial<UserSettings>): Promise<void> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const user = await userDb.getObj(userId);

  deleteUndefinedKeysInPlace(settings);

  return userDb.update(userId, {
    settings: {
      ...(user?.settings || {}),
      ...settings,
    } as UserSettings,
  });
}

export async function getCurrentTrainingPlan(userId: ID) {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  return userDb.getCurrentTrainingPlan(userId);
}

export async function saveCalendarEventForUser({
  userId,
  newEvent,
  originalEvent,
}: {
  userId: ID;
  newEvent: SaveEventPayload;
  originalEvent?: Event | null;
}): Promise<Event> {
  const { ...eventData } = newEvent;
  const endAt = calculateEventEndAt(newEvent);

  if (originalEvent?.id) {
    await updateEventForUser(userId, originalEvent.id, { ...eventData, endAt } as Partial<Event>);
    return {
      ...originalEvent,
      ...eventData,
      endAt,
      type: eventData.type,
    } as Event;
  }

  return createEventForUser(userId, { ...eventData, endAt } as Partial<Event>);
}

export async function createEventForUser(userId: ID, event: Partial<Event>): Promise<Event> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const eventDb = userDb.getEventDb(userId);
  return eventDb.create(event).then((doc) => toObj(doc) as Event);
}

export async function cancelTrainingPlan(userId: ID, planId: ID) {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const trainingPlanDb = userDb.getTrainingPlanDb(userId);
  const upcomingEvents = await userDb.getUpcomingTrainingEvents(userId, planId, new Date());

  return await Promise.all([
    ...upcomingEvents.map((event) => userDb.getEventDb(userId).deleteX(event.id)),
    trainingPlanDb.update(planId, {
      status: 'cancelled',
    }),
  ]);
}

export async function createTrainingPlan(userId: ID, plan: Partial<TrainingPlan>) {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');

  const provider = getClientProvider();
  const userDb = new UserDatabase({
    provider,
  });

  const trainingPlanDb = userDb.getTrainingPlanDb(userId);
  const newPlan = await trainingPlanDb.create(plan);

  await userDb.update(userId, {
    currentTrainingPlanId: newPlan.id,
  });

  return newPlan;
}

export async function createPromotionCampaign(
  userId: ID,
  type: CampaignApplicationType,
  event: Event,
  campaign: {
    raceName: string;
    selectedDistance: DistanceType;
    distanceInMeters?: number;
    officialRaceUrl?: string;
    uploadedProof: File;
  }
): Promise<CampaignApplication> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');
  const provider = getClientProvider();
  const userDb = new UserDatabase({ provider });
  const campaignApplicationDb = userDb.getCampaignApplicationDb(userId);
  const applicationDoc = await campaignApplicationDb.create({
    type,
    userId,
    raceName: campaign.raceName,
    distance: campaign.selectedDistance,
    distanceInMeters: campaign.selectedDistance === 'OTHER' ? campaign.distanceInMeters : 0,
    officialRaceUrl: campaign.officialRaceUrl,
    proofOfEntry: campaign.uploadedProof,
    status: CampaignApplicationStatus.PENDING,
    eventId: event.id,
  } as Partial<CampaignApplication>);

  return toObj(applicationDoc) as CampaignApplication;
}

export async function createAskACoachApplication(
  userId: ID,
  question: string,
  topicId: ID
): Promise<AskACoachApplication> {
  const { getClientProvider } = await import('./');
  const { UserDatabase } = await import('@shared/db');
  const provider = getClientProvider();
  const userDb = new UserDatabase({ provider });
  const campaignApplicationDb = userDb.getCampaignApplicationDb(userId);
  const applicationDoc = await campaignApplicationDb.create({
    type: CampaignApplicationType.ASK_A_COACH,
    userId,
    question,
    topicId,
    status: CampaignApplicationStatus.PENDING,
  } as Partial<AskACoachApplication>);

  return toObj(applicationDoc) as AskACoachApplication;
}
